import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import midnightEngineTrackUrl from "./assets/audio/MidnightEngine.mp3";
import {
  Board,
  type BoardIncrementLane,
  type BoardTimerDisplay,
  type FloatingScore,
  type PendingMove
} from "./components/Board";
import { GameOverModal } from "./components/GameOverModal";
import { HandView, type HandTileView } from "./components/HandView";
import { Header } from "./components/Header";
import { InfoModal } from "./components/InfoModal";
import { LeaderboardScreen } from "./components/LeaderboardScreen";
import { SettingsModal } from "./components/SettingsModal";
import type { SupportLinkKind } from "./components/SupportLinks";
import { StatusBanner, type StatusBannerTone } from "./components/StatusBanner";
import { TitleScreen } from "./components/TitleScreen";
import { TutorialRound } from "./components/TutorialRound";
import { createInitialState } from "./game/engine";
import { FLOW_WINDOW_MS, MIN_VISIBLE_FLOW_COUNT, isFlowActive } from "./game/flow";
import { createRandomTileGenerator } from "./game/generator";
import { resolveMove } from "./game/helpers";
import {
  DEFAULT_LEADERBOARD_METRIC,
  DEFAULT_LEADERBOARD_PERIOD,
  type LeaderboardMetric
} from "./game/leaderboard";
import {
  FIRST_RUN_INITIAL_DEAL,
  loadOnboardingState,
  saveOnboardingState,
  shouldShowRoundZeroTutorial,
  shouldUseGuidedRun,
  type OnboardingState
} from "./game/onboarding";
import { loadPersonalBests, savePersonalBests } from "./game/personalBestsStorage";
import { loadProfileName, saveProfileName } from "./game/profileStorage";
import {
  isIncrementEnabled,
  isNegativeEnabled,
  isRefillEnabled
} from "./game/progression";
import {
  REFILL_WINDOW_MS,
  hasRefillSpace
} from "./game/refill";
import { reduceGame } from "./game/reducers";
import { createSeededRandom } from "./game/random";
import { configureTelemetrySink, emitTelemetry } from "./game/telemetry";
import {
  alignRunProofToServerTimeline,
  getLeaderboardSaveReviewMessage,
  startLeaderboardRun,
  submitLeaderboardRun,
  type LeaderboardEntry,
  type VerifiedRunAction,
  type VerifiedRunPauseWindow
} from "./lib/leaderboard";
import { APP_VERSION, formatBuildLabel } from "./lib/buildInfo";
import { GAME_RULES_VERSION } from "../../../shared/gameRules.ts";
import { isAudioMuted, toggleAudioMutedVolumes } from "./lib/audioControls";
import {
  getPendingFeedbackPromptThreshold,
  loadFeedbackPromptState,
  markFeedbackPromptShown,
  markFeedbackSubmitted,
  recordCompletedRun,
  saveFeedbackPromptState,
  type FeedbackPromptRunThreshold
} from "./lib/feedbackPrompt";
import { ensureAnonymousSession, getSupabaseClient } from "./lib/supabase";
import { TelemetryClient } from "./lib/telemetry";
import {
  getDefaultRuntimeConfig,
  loadRuntimeConfig,
  type RuntimeConfig
} from "./lib/runtimeConfig";
import { getBackgroundPreparedRunGuidance } from "./lib/runPreparation";
import {
  createMusicController,
  shouldMusicBeActive,
  type MusicController,
  type MusicPlaybackState,
  type MusicScreen
} from "./lib/musicController";
import {
  DEFAULT_MUSIC_VOLUME_PERCENT,
  loadMusicVolume,
  saveMusicVolume
} from "./lib/musicSettings";
import {
  createSoundEffectController,
  type SoundEffectController
} from "./lib/soundEffectController";
import {
  DEFAULT_SFX_VOLUME_PERCENT,
  loadSoundEffectVolume,
  saveSoundEffectVolume
} from "./lib/soundEffectSettings";
import { getMoveSoundEffectId, type SoundEffectId } from "./lib/soundEffects";
import type { GameAction, GameState, ScoreEvent } from "./game/types";
import { getTileColor, type Theme } from "./theme";

type AppModel = {
  game: GameState;
  handTiles: HandTileView[];
  floatingScores: FloatingScore[];
};

type AppScreen = MusicScreen;
type LeaderboardHighlightEntries = Partial<Record<LeaderboardMetric, LeaderboardEntry>>;
type MechanicSpotlight =
  | { kind: "flow" }
  | { kind: "negative"; handTileId: string }
  | { kind: "increment"; boardTileId: string }
  | { kind: "refill" };
type PendingLeaderboardSubmission = {
  actions: VerifiedRunAction[];
  appVersion: string;
  completedAt: number;
  token: string;
  playerName: string;
  runId: string;
};
type ActiveRunSession = {
  clientStartedAt: number;
  runId: string;
  runSeed: string;
  startedAt: number;
  guidedRun: boolean;
};
type PreparedRun = {
  guidedRun: boolean;
  preparedAt: number;
  runId: string;
  runSeed: string;
  session: ActiveRunSession | null;
};
type AppStatusBanner = {
  title: string;
  body: string;
  tone: StatusBannerTone;
};

const IDLE_TUTORIAL_DELAY_MS = 4000;
const GAME_OVER_ANNOUNCEMENT_MS = 2400;
const PREPARED_RUN_MAX_AGE_MS = 5 * 60 * 1000;
const GAME_SCREEN_TRANSITION = {
  duration: 0.18,
  ease: "easeOut" as const
};

function createRunId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getFlowDurationMs(game: GameState): number {
  if (game.flow.expiresAt === null || game.flow.lastMoveAt === null) {
    return FLOW_WINDOW_MS;
  }

  return Math.max(FLOW_WINDOW_MS, game.flow.expiresAt - game.flow.lastMoveAt);
}

function mapHandTiles(hand: readonly number[], nextId: () => string): HandTileView[] {
  return hand.map((value) => ({ id: nextId(), value }));
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
}

function buildHighlightEntries(
  entries: readonly LeaderboardEntry[]
): LeaderboardHighlightEntries {
  return entries.reduce<LeaderboardHighlightEntries>((highlightEntries, entry) => {
    highlightEntries[entry.metricType] = entry;
    return highlightEntries;
  }, {});
}

export default function App() {
  const initialRunIdRef = useRef(createRunId());
  const generatorRef = useRef(
    createRandomTileGenerator(createSeededRandom(initialRunIdRef.current))
  );
  const telemetryRef = useRef<TelemetryClient | null>(null);
  if (telemetryRef.current === null) {
    telemetryRef.current = new TelemetryClient();
  }
  const telemetry = telemetryRef.current;

  const handTileIdCounterRef = useRef(0);
  const floatingScoreIdCounterRef = useRef(0);
  const floatingScoreLaneRef = useRef(0);
  const pendingPlayedHandIndexRef = useRef<number | null>(null);
  const moveTimerRef = useRef<number | null>(null);
  const shakeTimerRef = useRef<number | null>(null);
  const flowExpiryTimerRef = useRef<number | null>(null);
  const refillExpiryTimerRef = useRef<number | null>(null);
  const musicControllerRef = useRef<MusicController | null>(null);
  const soundEffectControllerRef = useRef<SoundEffectController | null>(null);
  const incrementExpiryTimersRef = useRef(
    new Map<number, { timeoutId: number; expiresAt: number }>()
  );
  const statusBannerTimerRef = useRef<number | null>(null);
  const tutorialHintTimerRef = useRef<number | null>(null);
  const gameOverRevealTimerRef = useRef<number | null>(null);
  const floatingScoreTimersRef = useRef(new Map<string, number>());
  const preparedRunRef = useRef<PreparedRun | null>(null);
  const preparedRunRequestRef = useRef<{
    guidedRun: boolean;
    promise: Promise<PreparedRun>;
  } | null>(null);
  const previousGameOverRef = useRef(false);
  const previousFlowCountRef = useRef(0);
  const previousRoundsClearedRef = useRef(0);
  const hasTrackedAppLoadRef = useRef(false);
  const lastTrackedScreenRef = useRef<string | null>(null);
  const recordedRunActionsRef = useRef<VerifiedRunAction[]>([]);
  const recordedRunPauseWindowsRef = useRef<VerifiedRunPauseWindow[]>([]);
  const pendingRunSessionErrorRef = useRef<string | null>(null);
  const latestGameRef = useRef<GameState | null>(null);
  const isDocumentVisibleRef = useRef(true);
  const isTimerPausedRef = useRef(false);
  const sfxVolumePercentRef = useRef(DEFAULT_SFX_VOLUME_PERCENT);

  const nextHandTileId = () => `h-${handTileIdCounterRef.current++}`;
  const nextFloatingScoreId = () => `s-${floatingScoreIdCounterRef.current++}`;
  const nextFloatingScoreLane = () => {
    const lane = floatingScoreLaneRef.current % 3;
    floatingScoreLaneRef.current += 1;
    return lane;
  };

  const setRunGenerator = (seed: string) => {
    generatorRef.current = createRandomTileGenerator(createSeededRandom(seed));
  };

  const recordVerifiedRunAction = (action: VerifiedRunAction) => {
    const previousAction = recordedRunActionsRef.current[recordedRunActionsRef.current.length - 1];
    if (
      previousAction?.handIndex === action.handIndex &&
      previousAction.boardIndex === action.boardIndex &&
      previousAction.playedAt === action.playedAt
    ) {
      return;
    }

    recordedRunActionsRef.current = [...recordedRunActionsRef.current, action];
  };

  const recordVerifiedRunPauseWindow = (pauseWindow: VerifiedRunPauseWindow) => {
    if (pauseWindow.endedAt <= pauseWindow.startedAt) {
      return;
    }

    const previousPauseWindow =
      recordedRunPauseWindowsRef.current[recordedRunPauseWindowsRef.current.length - 1];
    if (
      previousPauseWindow?.startedAt === pauseWindow.startedAt &&
      previousPauseWindow.endedAt === pauseWindow.endedAt
    ) {
      return;
    }

    recordedRunPauseWindowsRef.current = [
      ...recordedRunPauseWindowsRef.current,
      pauseWindow
    ];
  };

  const theme: Theme = "neon-puzzle";
  const [screen, setScreen] = useState<AppScreen>("title");
  const [screenShake, setScreenShake] = useState(false);
  const [isResolvingMove, setIsResolvingMove] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<PendingMove>(null);
  const [activeLeaderboardMetric, setActiveLeaderboardMetric] =
    useState<LeaderboardMetric>(DEFAULT_LEADERBOARD_METRIC);
  const [activeLeaderboardPeriod, setActiveLeaderboardPeriod] =
    useState(DEFAULT_LEADERBOARD_PERIOD);
  const [highlightLeaderboardEntries, setHighlightLeaderboardEntries] =
    useState<LeaderboardHighlightEntries>({});
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0);
  const [isSavingLeaderboardScore, setIsSavingLeaderboardScore] = useState(false);
  const [leaderboardSaveError, setLeaderboardSaveError] = useState<string | null>(null);
  const [leaderboardSaveNotice, setLeaderboardSaveNotice] = useState<string | null>(null);
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig>(() => getDefaultRuntimeConfig());
  const [isLoadingRuntimeConfig, setIsLoadingRuntimeConfig] = useState(true);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => {
    if (typeof document === "undefined") {
      return true;
    }

    return document.visibilityState !== "hidden";
  });
  const [musicVolumePercent, setMusicVolumePercent] = useState(() => loadMusicVolume());
  const [sfxVolumePercent, setSfxVolumePercent] = useState(() => loadSoundEffectVolume());
  const [profileName, setProfileName] = useState(() => loadProfileName());
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState(initialRunIdRef.current);
  const [activeRunSession, setActiveRunSession] = useState<ActiveRunSession | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingState>(() => loadOnboardingState());
  const [feedbackPromptState, setFeedbackPromptState] = useState(() => loadFeedbackPromptState());
  const [activeFeedbackPromptThreshold, setActiveFeedbackPromptThreshold] =
    useState<FeedbackPromptRunThreshold | null>(null);
  const initialGuidedRun = shouldUseGuidedRun(onboarding);
  const [isGuidedRun, setIsGuidedRun] = useState(initialGuidedRun);
  const [statusBanner, setStatusBanner] = useState<AppStatusBanner | null>(null);
  const [tutorialHintText, setTutorialHintText] = useState<string | null>(null);
  const [tutorialActivityKey, setTutorialActivityKey] = useState(0);
  const [activeMechanicSpotlight, setActiveMechanicSpotlight] =
    useState<MechanicSpotlight | null>(null);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [isTutorialRoundActive, setIsTutorialRoundActive] = useState(false);
  const [timerSpotlightPauseStartedAt, setTimerSpotlightPauseStartedAt] = useState<number | null>(
    null
  );
  const [showDeadlockAnnouncement, setShowDeadlockAnnouncement] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [pendingLeaderboardSubmission, setPendingLeaderboardSubmission] =
    useState<PendingLeaderboardSubmission | null>(null);
  const [model, setModel] = useState<AppModel>(() => {
    const personalBests = loadPersonalBests();
    const game = createInitialState(
      generatorRef.current,
      personalBests.bestRound,
      personalBests.bestScore,
      personalBests.bestMaxFlow,
      initialGuidedRun ? FIRST_RUN_INITIAL_DEAL : null
    );
    return {
      game,
      handTiles: mapHandTiles(game.hand, nextHandTileId),
      floatingScores: []
    };
  });

  const { game, handTiles } = model;
  const buildLabel = formatBuildLabel();
  const isBetaGateVisible = false;
  const lastAudibleMusicVolumeRef = useRef(
    musicVolumePercent > 0 ? musicVolumePercent : DEFAULT_MUSIC_VOLUME_PERCENT
  );
  const lastAudibleSfxVolumeRef = useRef(
    sfxVolumePercent > 0 ? sfxVolumePercent : DEFAULT_SFX_VOLUME_PERCENT
  );

  latestGameRef.current = game;
  isDocumentVisibleRef.current = isDocumentVisible;
  sfxVolumePercentRef.current = sfxVolumePercent;

  const ensureMusicController = () => {
    if (musicControllerRef.current === null) {
      musicControllerRef.current = createMusicController(midnightEngineTrackUrl);
    }

    return musicControllerRef.current;
  };

  const ensureSoundEffectController = () => {
    if (soundEffectControllerRef.current === null) {
      soundEffectControllerRef.current = createSoundEffectController();
      soundEffectControllerRef.current.syncVolume(sfxVolumePercentRef.current);
    }

    return soundEffectControllerRef.current;
  };

  const playSoundEffect = (soundEffectId: SoundEffectId) => {
    if (sfxVolumePercentRef.current === 0 || !isDocumentVisibleRef.current) {
      return;
    }

    ensureSoundEffectController().play(soundEffectId);
  };

  const getMusicPlaybackState = (
    overrides: Partial<MusicPlaybackState> = {}
  ): MusicPlaybackState => ({
    isBetaGateVisible,
    isDocumentVisible,
    isTutorialRoundActive,
    musicVolumePercent,
    screen,
    ...overrides
  });

  const activateGameplayMusic = () => {
    ensureMusicController().activatePlayback(
      getMusicPlaybackState({
        isTutorialRoundActive: false,
        screen: "game"
      })
    );
  };

  const updateOnboarding = (patch: Partial<OnboardingState>) => {
    setOnboarding((previousState) =>
      saveOnboardingState({
        ...previousState,
        ...patch
      })
    );
  };

  const clearStatusBanner = () => {
    if (statusBannerTimerRef.current !== null) {
      window.clearTimeout(statusBannerTimerRef.current);
      statusBannerTimerRef.current = null;
    }

    setStatusBanner(null);
  };

  const clearGameOverReveal = () => {
    if (gameOverRevealTimerRef.current !== null) {
      window.clearTimeout(gameOverRevealTimerRef.current);
      gameOverRevealTimerRef.current = null;
    }
  };

  const showStatusBanner = (nextBanner: AppStatusBanner) => {
    if (statusBannerTimerRef.current !== null) {
      window.clearTimeout(statusBannerTimerRef.current);
    }

    setStatusBanner(nextBanner);
    statusBannerTimerRef.current = window.setTimeout(() => {
      setStatusBanner(null);
      statusBannerTimerRef.current = null;
    }, nextBanner.tone === "warning" ? 1500 : 2600);
  };

  const clearTutorialHint = () => {
    if (tutorialHintTimerRef.current !== null) {
      window.clearTimeout(tutorialHintTimerRef.current);
      tutorialHintTimerRef.current = null;
    }

    setTutorialHintText(null);
  };

  const markTutorialActivity = () => {
    clearTutorialHint();
    setTutorialActivityKey((previousValue) => previousValue + 1);
  };

  useEffect(() => {
    return () => {
      if (moveTimerRef.current !== null) {
        window.clearTimeout(moveTimerRef.current);
      }

      if (shakeTimerRef.current !== null) {
        window.clearTimeout(shakeTimerRef.current);
      }

      if (flowExpiryTimerRef.current !== null) {
        window.clearTimeout(flowExpiryTimerRef.current);
      }

      if (refillExpiryTimerRef.current !== null) {
        window.clearTimeout(refillExpiryTimerRef.current);
      }

      incrementExpiryTimersRef.current.forEach(({ timeoutId }) => {
        window.clearTimeout(timeoutId);
      });
      incrementExpiryTimersRef.current.clear();

      if (statusBannerTimerRef.current !== null) {
        window.clearTimeout(statusBannerTimerRef.current);
      }

      if (tutorialHintTimerRef.current !== null) {
        window.clearTimeout(tutorialHintTimerRef.current);
      }

      if (gameOverRevealTimerRef.current !== null) {
        window.clearTimeout(gameOverRevealTimerRef.current);
      }

      floatingScoreTimersRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      floatingScoreTimersRef.current.clear();

      if (musicControllerRef.current !== null) {
        musicControllerRef.current.dispose();
        musicControllerRef.current = null;
      }

      if (soundEffectControllerRef.current !== null) {
        soundEffectControllerRef.current.dispose();
        soundEffectControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const nextConfig = await loadRuntimeConfig();
        if (!cancelled) {
          setRuntimeConfig(nextConfig);
        }
      } catch (error) {
        if (!cancelled) {
          const fallbackConfig = getDefaultRuntimeConfig();
          setRuntimeConfig(fallbackConfig);
          telemetry.captureError("runtime_config_load", error, {
            fallbackBetaGateEnabled: fallbackConfig.betaGateEnabled
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRuntimeConfig(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [telemetry]);

  useEffect(() => {
    configureTelemetrySink((event) => {
      telemetry.track(event.name, event.payload);
    });

    return () => {
      configureTelemetrySink(null);
    };
  }, [telemetry]);

  useEffect(() => {
    telemetry.updateContext({
      anonymousUserId: currentUserId,
      errorMonitoringEnabled: runtimeConfig.errorMonitoringEnabled,
      feedbackSurveyEnabled: runtimeConfig.feedbackSurveyEnabled,
      inviteCohort: null,
      productAnalyticsEnabled: runtimeConfig.productAnalyticsEnabled,
      runId: screen === "game" ? activeRunId : null,
      screen,
      sessionReplayEnabled: runtimeConfig.sessionReplayEnabled,
      telemetryEnabled: runtimeConfig.telemetryEnabled
    });
  }, [
    activeRunId,
    currentUserId,
    runtimeConfig.errorMonitoringEnabled,
    runtimeConfig.feedbackSurveyEnabled,
    runtimeConfig.productAnalyticsEnabled,
    runtimeConfig.sessionReplayEnabled,
    runtimeConfig.telemetryEnabled,
    screen,
    telemetry
  ]);

  useEffect(() => {
    if (isLoadingRuntimeConfig || hasTrackedAppLoadRef.current) {
      return;
    }

    telemetry.track("app_loaded", {
      betaGateEnabled: runtimeConfig.betaGateEnabled,
      errorMonitoringEnabled: runtimeConfig.errorMonitoringEnabled,
      feedbackSurveyEnabled: runtimeConfig.feedbackSurveyEnabled,
      leaderboardEnabled: runtimeConfig.leaderboardEnabled,
      productAnalyticsEnabled: runtimeConfig.productAnalyticsEnabled,
      sessionReplayEnabled: runtimeConfig.sessionReplayEnabled,
      telemetryEnabled: runtimeConfig.telemetryEnabled
    });
    hasTrackedAppLoadRef.current = true;
  }, [isLoadingRuntimeConfig, runtimeConfig, telemetry]);

  useEffect(() => {
    if (isLoadingRuntimeConfig) {
      return;
    }

    const nextScreen = screen;
    if (lastTrackedScreenRef.current === nextScreen) {
      return;
    }

    telemetry.track("screen_viewed", {
      previousScreen: lastTrackedScreenRef.current,
      screen: nextScreen
    });
    lastTrackedScreenRef.current = nextScreen;
  }, [isLoadingRuntimeConfig, screen, telemetry]);

  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      telemetry.captureError("window.error", event.error ?? event.message, {
        filename: event.filename || null,
        line: event.lineno,
        column: event.colno
      }, {
        forwardToSentry: false
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      telemetry.captureError("window.unhandledrejection", event.reason, {}, {
        forwardToSentry: false
      });
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [telemetry]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState !== "hidden");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Warm the anonymous session in the background so leaderboard actions usually have an owner ready.
    if (!runtimeConfig.leaderboardEnabled && !runtimeConfig.telemetryEnabled) {
      return;
    }

    let cancelled = false;

    void ensureAnonymousSession().then((user) => {
      if (!cancelled) {
        setCurrentUserId(user?.id ?? null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [runtimeConfig.leaderboardEnabled, runtimeConfig.telemetryEnabled]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    void getSupabaseClient().then((client) => {
      if (cancelled || client === null) {
        return;
      }

      const {
        data: { subscription }
      } = client.auth.onAuthStateChange((_event, session) => {
        if (!cancelled) {
          setCurrentUserId(session?.user?.id ?? null);
        }
      });

      unsubscribe = () => {
        subscription.unsubscribe();
      };
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (screen === "leaderboard" && !runtimeConfig.leaderboardEnabled) {
      setScreen("title");
    }
  }, [runtimeConfig.leaderboardEnabled, screen]);

  useEffect(() => {
    const musicPlaybackState = getMusicPlaybackState();
    if (musicControllerRef.current === null && !shouldMusicBeActive(musicPlaybackState)) {
      return;
    }

    ensureMusicController().syncPlayback(musicPlaybackState);
  }, [isBetaGateVisible, isDocumentVisible, isTutorialRoundActive, musicVolumePercent, screen]);

  const selectedHandValue =
    game.selectedHandIndex === null ? null : game.hand[game.selectedHandIndex] ?? null;
  const negativeHandIndex = game.hand.findIndex((value) => value < 0);
  const negativeHandTile = negativeHandIndex >= 0 ? handTiles[negativeHandIndex] ?? null : null;
  const isMechanicSpotlightOpen = activeMechanicSpotlight !== null;
  const isBoardMechanicSpotlightOpen =
    activeMechanicSpotlight?.kind === "flow" ||
    activeMechanicSpotlight?.kind === "increment" ||
    activeMechanicSpotlight?.kind === "refill";
  const isTimerPaused = isMechanicSpotlightOpen && timerSpotlightPauseStartedAt !== null;
  isTimerPausedRef.current = isTimerPaused;
  const isInteractionLocked =
    isResolvingMove || isSettingsOpen || isRulesOpen || activeMechanicSpotlight !== null;
  const showFlowTimer = !game.gameOver && isFlowActive(game.flow) && game.flow.expiresAt !== null;
  const showRefillTimer =
    !game.gameOver &&
    !showFlowTimer &&
    game.refill.timerVisible &&
    hasRefillSpace(game.board) &&
    activeMechanicSpotlight?.kind !== "refill";
  const isLiveGameplayScreen = screen === "game" && !isTutorialRoundActive;
  const shouldShowTutorialHints =
    isLiveGameplayScreen &&
    !game.gameOver &&
    !isSettingsOpen &&
    !isResolvingMove &&
    !isRulesOpen &&
    activeMechanicSpotlight === null &&
    isGuidedRun &&
    !onboarding.hasCompletedFirstRun &&
    !onboarding.hasSeenTutorial;
  const tutorialHintPhase = !shouldShowTutorialHints
    ? null
    : game.selectedHandIndex === null
      ? "hand"
      : "board";
  let boardTimerDisplay: BoardTimerDisplay | null = null;

  if (activeMechanicSpotlight?.kind === "negative") {
    boardTimerDisplay = null;
  } else if (activeMechanicSpotlight?.kind === "refill") {
    boardTimerDisplay = {
      kind: "refill",
      expiresAt: null,
      durationMs: REFILL_WINDOW_MS
    };
  } else if (showFlowTimer) {
    boardTimerDisplay = {
      kind: "flow",
      flowCount: game.flow.count,
      expiresAt: game.flow.expiresAt ?? Date.now(),
      durationMs: getFlowDurationMs(game)
    };
  } else if (showRefillTimer) {
    boardTimerDisplay = {
      kind: "refill",
      expiresAt: game.refill.expiresAt,
      durationMs: REFILL_WINDOW_MS
    };
  }

  const boardIncrementLanes: BoardIncrementLane[] = game.increment.lanes.reduce<
    BoardIncrementLane[]
  >((lanes, lane, laneIndex) => {
    if (lane.tileId === null || lane.expiresAt === null) {
      return lanes;
    }

    lanes.push({
      laneIndex,
      tileId: lane.tileId,
      expiresAt: lane.expiresAt,
      durationMs: lane.durationMs,
      burstCount: lane.burstCount
    });
    return lanes;
  }, []);
  const audioMuted = isAudioMuted({ musicVolumePercent, sfxVolumePercent });

  const applyGameAction = (action: GameAction) => {
    setModel((previousModel) => {
      const result = reduceGame(previousModel.game, action, generatorRef.current);
      emitTelemetry(result.events);

      const pendingPlayedHandIndex = pendingPlayedHandIndexRef.current;
      let nextHandTiles = previousModel.handTiles;
      let nextFloatingScores = previousModel.floatingScores;

      const committedLegalMove =
        action.type === "tap_board_tile" &&
        pendingPlayedHandIndex !== null &&
        result.state.turnIndex === previousModel.game.turnIndex + 1;

      if (committedLegalMove) {
        recordVerifiedRunAction({
          handIndex: pendingPlayedHandIndex,
          boardIndex: action.boardIndex,
          playedAt: action.playedAt
        });
      }

      if (committedLegalMove && result.state.roundNumber === previousModel.game.roundNumber) {
        const survivingTiles = previousModel.handTiles.filter(
          (_, handIndex) => handIndex !== pendingPlayedHandIndex
        );

        if (survivingTiles.length === 2) {
          nextHandTiles = [
            { ...survivingTiles[0], value: result.state.hand[0] },
            { ...survivingTiles[1], value: result.state.hand[1] },
            { id: nextHandTileId(), value: result.state.hand[2] }
          ];
        } else {
          nextHandTiles = mapHandTiles(result.state.hand, nextHandTileId);
        }
      } else if (
        action.type === "restart_run" ||
        result.state.roundNumber !== previousModel.game.roundNumber ||
        result.state.turnIndex < previousModel.game.turnIndex
      ) {
        nextHandTiles = mapHandTiles(result.state.hand, nextHandTileId);
      } else {
        nextHandTiles = previousModel.handTiles.map((tile, index) => ({
          ...tile,
          value: result.state.hand[index] ?? tile.value
        }));
      }

      if (result.scoreEvents.length > 0) {
        nextFloatingScores = [
          ...previousModel.floatingScores,
          ...createFloatingScores(result.scoreEvents, previousModel.game.board.length)
        ];
      }

      if (action.type === "tap_board_tile" || action.type === "restart_run") {
        pendingPlayedHandIndexRef.current = null;
      }

      return {
        game: result.state,
        handTiles: nextHandTiles,
        floatingScores: nextFloatingScores
      };
    });
  };

  useEffect(() => {
    if (flowExpiryTimerRef.current !== null) {
      window.clearTimeout(flowExpiryTimerRef.current);
      flowExpiryTimerRef.current = null;
    }

    if (isTimerPaused || game.flow.count === 0 || game.flow.expiresAt === null) {
      return;
    }

    const remainingMs = game.flow.expiresAt - Date.now();
    if (remainingMs <= 0) {
      applyGameAction({ type: "flow_window_expired", expiresAt: game.flow.expiresAt });
      return;
    }

    const expiresAt = game.flow.expiresAt;
    flowExpiryTimerRef.current = window.setTimeout(() => {
      applyGameAction({ type: "flow_window_expired", expiresAt });
      flowExpiryTimerRef.current = null;
    }, remainingMs);

    return () => {
      if (flowExpiryTimerRef.current !== null) {
        window.clearTimeout(flowExpiryTimerRef.current);
        flowExpiryTimerRef.current = null;
      }
    };
  }, [game.flow.count, game.flow.expiresAt, isTimerPaused]);

  useEffect(() => {
    if (game.flow.expiresAt !== null) {
      return;
    }

    setTimerSpotlightPauseStartedAt(null);
  }, [game.flow.expiresAt]);

  useEffect(() => {
    if (refillExpiryTimerRef.current !== null) {
      window.clearTimeout(refillExpiryTimerRef.current);
      refillExpiryTimerRef.current = null;
    }

    if (game.gameOver || game.refill.expiresAt === null || isResolvingMove) {
      return;
    }

    const remainingMs = game.refill.expiresAt - Date.now();
    if (remainingMs <= 0) {
      const currentGame = latestGameRef.current;
      const expiresAt = game.refill.expiresAt;
      if (
        currentGame !== null &&
        !currentGame.gameOver &&
        currentGame.refill.active &&
        currentGame.refill.expiresAt === expiresAt &&
        hasRefillSpace(currentGame.board)
      ) {
        applyGameAction({ type: "refill_window_expired", expiresAt });
        playSoundEffect("refill");
      }

      return;
    }

    const expiresAt = game.refill.expiresAt;
    refillExpiryTimerRef.current = window.setTimeout(() => {
      const currentGame = latestGameRef.current;
      if (
        currentGame !== null &&
        !currentGame.gameOver &&
        currentGame.refill.active &&
        currentGame.refill.expiresAt === expiresAt &&
        hasRefillSpace(currentGame.board)
      ) {
        applyGameAction({ type: "refill_window_expired", expiresAt });
        playSoundEffect("refill");
      }

      refillExpiryTimerRef.current = null;
    }, remainingMs);

    return () => {
      if (refillExpiryTimerRef.current !== null) {
        window.clearTimeout(refillExpiryTimerRef.current);
        refillExpiryTimerRef.current = null;
      }
    };
  }, [game.gameOver, game.refill.expiresAt, isResolvingMove]);

  useEffect(() => {
    incrementExpiryTimersRef.current.forEach(({ timeoutId }) => {
      window.clearTimeout(timeoutId);
    });
    incrementExpiryTimersRef.current.clear();

    if (game.gameOver || isTimerPaused || isResolvingMove) {
      return;
    }

    game.increment.lanes.forEach((lane, laneIndex) => {
      if (lane.tileId === null || lane.expiresAt === null) {
        return;
      }

      const remainingMs = lane.expiresAt - Date.now();
      if (remainingMs <= 0) {
        const currentGame = latestGameRef.current;
        const currentLane = currentGame?.increment.lanes[laneIndex];
        const targetTile =
          currentLane?.tileId === null || currentLane?.tileId === undefined
            ? null
            : currentGame?.board.find((tile) => tile.id === currentLane.tileId) ?? null;
        const shouldApplyIncrement =
          currentGame !== null &&
          !currentGame.gameOver &&
          !isTimerPausedRef.current &&
          currentLane !== undefined &&
          currentLane.expiresAt === lane.expiresAt;
        const shouldPlayIncrementSound =
          shouldApplyIncrement &&
          targetTile !== null &&
          targetTile.value < 9;

        if (shouldApplyIncrement) {
          applyGameAction({
            type: "increment_window_expired",
            laneIndex,
            expiresAt: lane.expiresAt
          });
          if (shouldPlayIncrementSound) {
            playSoundEffect("increment");
          }
        }

        return;
      }

      const expiresAt = lane.expiresAt;
      const timeoutId = window.setTimeout(() => {
        const currentGame = latestGameRef.current;
        const currentLane = currentGame?.increment.lanes[laneIndex];
        const targetTile =
          currentLane?.tileId === null || currentLane?.tileId === undefined
            ? null
            : currentGame?.board.find((tile) => tile.id === currentLane.tileId) ?? null;
        const shouldApplyIncrement =
          currentGame !== null &&
          !currentGame.gameOver &&
          !isTimerPausedRef.current &&
          currentLane !== undefined &&
          currentLane.expiresAt === expiresAt;
        const shouldPlayIncrementSound =
          shouldApplyIncrement &&
          targetTile !== null &&
          targetTile.value < 9;

        if (shouldApplyIncrement) {
          applyGameAction({
            type: "increment_window_expired",
            laneIndex,
            expiresAt
          });
          if (shouldPlayIncrementSound) {
            playSoundEffect("increment");
          }
        }
      }, remainingMs);

      incrementExpiryTimersRef.current.set(laneIndex, { timeoutId, expiresAt });
    });

    return () => {
      incrementExpiryTimersRef.current.forEach(({ timeoutId }) => {
        window.clearTimeout(timeoutId);
      });
      incrementExpiryTimersRef.current.clear();
    };
  }, [game.gameOver, game.increment.lanes, isResolvingMove, isTimerPaused]);

  useEffect(() => {
    model.floatingScores.forEach((floatingScore) => {
      if (floatingScoreTimersRef.current.has(floatingScore.id)) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        floatingScoreTimersRef.current.delete(floatingScore.id);
        setModel((previousModel) => {
          if (!previousModel.floatingScores.some((score) => score.id === floatingScore.id)) {
            return previousModel;
          }

          return {
            ...previousModel,
            floatingScores: previousModel.floatingScores.filter(
              (score) => score.id !== floatingScore.id
            )
          };
        });
      }, 720 + floatingScore.delayMs);

      floatingScoreTimersRef.current.set(floatingScore.id, timeoutId);
    });
  }, [model.floatingScores]);

  useEffect(() => {
    savePersonalBests({
      bestScore: game.bestScore,
      bestRound: game.bestRound,
      bestMaxFlow: game.bestMaxFlow
    });
  }, [game.bestMaxFlow, game.bestRound, game.bestScore]);

  useEffect(() => {
    if (!previousGameOverRef.current && game.gameOver && game.lastRunSummary !== null) {
      setLeaderboardSaveError(null);
      setLeaderboardSaveNotice(null);
      setHighlightLeaderboardEntries({});

      if (runtimeConfig.leaderboardEnabled) {
        if (activeRunSession === null) {
          setPendingLeaderboardSubmission(null);
          setLeaderboardSaveError(
            pendingRunSessionErrorRef.current ??
              "Verified leaderboard session was unavailable for this run."
          );
          setLeaderboardSaveNotice(null);
        } else {
          const normalizedRunProof = alignRunProofToServerTimeline(
            recordedRunActionsRef.current,
            Date.now(),
            recordedRunPauseWindowsRef.current,
            activeRunSession.clientStartedAt,
            activeRunSession.startedAt
          );
          setPendingLeaderboardSubmission({
            actions: normalizedRunProof.actions,
            appVersion: APP_VERSION,
            completedAt: normalizedRunProof.completedAt,
            token: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
            playerName: profileName,
            runId: activeRunSession.runId
          });
        }
      } else if (!runtimeConfig.leaderboardEnabled) {
        setPendingLeaderboardSubmission(null);
        setLeaderboardSaveError("Global leaderboard sync is paused for this build.");
        setLeaderboardSaveNotice(null);
      }

      const nextFeedbackPromptState = recordCompletedRun(feedbackPromptState);
      let persistedFeedbackPromptState = nextFeedbackPromptState;
      let nextFeedbackPromptThreshold: FeedbackPromptRunThreshold | null = null;

      if (runtimeConfig.feedbackSurveyEnabled && runtimeConfig.feedbackUrl.trim().length > 0) {
        nextFeedbackPromptThreshold = getPendingFeedbackPromptThreshold(nextFeedbackPromptState);
        if (nextFeedbackPromptThreshold !== null) {
          persistedFeedbackPromptState = markFeedbackPromptShown(
            nextFeedbackPromptState,
            nextFeedbackPromptThreshold
          );
        }
      }

      const savedFeedbackPromptState = saveFeedbackPromptState(persistedFeedbackPromptState);
      setFeedbackPromptState(savedFeedbackPromptState);
      setActiveFeedbackPromptThreshold(nextFeedbackPromptThreshold);

      clearGameOverReveal();
      setShowGameOverModal(false);
      setShowDeadlockAnnouncement(true);
      gameOverRevealTimerRef.current = window.setTimeout(() => {
        setShowDeadlockAnnouncement(false);
        setShowGameOverModal(true);
        gameOverRevealTimerRef.current = null;
      }, GAME_OVER_ANNOUNCEMENT_MS);
    } else if (!game.gameOver) {
      clearGameOverReveal();
      setActiveFeedbackPromptThreshold(null);
      setShowDeadlockAnnouncement(false);
      setShowGameOverModal(false);
    }

    previousGameOverRef.current = game.gameOver;
  }, [
    activeRunSession,
    feedbackPromptState,
    game.gameOver,
    game.lastRunSummary,
    profileName,
    runtimeConfig.feedbackSurveyEnabled,
    runtimeConfig.feedbackUrl,
    runtimeConfig.leaderboardEnabled
  ]);

  useEffect(() => {
    if (game.roundsCleared > 0 && previousRoundsClearedRef.current === 0) {
      telemetry.track("first_round_cleared", {
        roundReached: game.roundNumber,
        score: game.score
      });
    }

    previousRoundsClearedRef.current = game.roundsCleared;
  }, [game.roundNumber, game.roundsCleared, game.score, telemetry]);

  useEffect(() => {
    if (!isGuidedRun || onboarding.hasCompletedFirstRun) {
      return;
    }

    if (game.roundNumber > 1 || game.gameOver) {
      updateOnboarding({ hasCompletedFirstRun: true });
    }
  }, [game.gameOver, game.roundNumber, isGuidedRun, onboarding.hasCompletedFirstRun]);

  useEffect(() => {
    clearTutorialHint();

    if (tutorialHintPhase === null) {
      return;
    }

    tutorialHintTimerRef.current = window.setTimeout(() => {
      setTutorialHintText(
        tutorialHintPhase === "hand"
          ? "Tap a tile in your hand"
          : "Tap a matching tile or add to 10 to clear"
      );
      tutorialHintTimerRef.current = null;
    }, IDLE_TUTORIAL_DELAY_MS);

    return () => {
      if (tutorialHintTimerRef.current !== null) {
        window.clearTimeout(tutorialHintTimerRef.current);
        tutorialHintTimerRef.current = null;
      }
    };
  }, [tutorialActivityKey, tutorialHintPhase]);

  useEffect(() => {
    const flowJustActivated =
      game.flow.count >= MIN_VISIBLE_FLOW_COUNT &&
      previousFlowCountRef.current < MIN_VISIBLE_FLOW_COUNT;

    if (
      flowJustActivated &&
      !onboarding.hasSeenFlowExplanation &&
      isLiveGameplayScreen &&
      !game.gameOver &&
      activeMechanicSpotlight === null
    ) {
      telemetry.track("tutorial_progress", {
        step: "flow_explainer_opened"
      });
      clearStatusBanner();
      clearTutorialHint();
      setActiveMechanicSpotlight({ kind: "flow" });
      setTimerSpotlightPauseStartedAt(Date.now());
    }

    previousFlowCountRef.current = game.flow.count;
  }, [
    activeMechanicSpotlight,
    game.flow.count,
    game.gameOver,
    isLiveGameplayScreen,
    onboarding.hasSeenFlowExplanation,
    telemetry
  ]);

  useEffect(() => {
    if (
      !isLiveGameplayScreen ||
      game.gameOver ||
      onboarding.hasSeenNegativeExplanation ||
      !isNegativeEnabled({ roundNumber: game.roundNumber, score: game.score }) ||
      activeMechanicSpotlight !== null ||
      negativeHandTile === null
    ) {
      return;
    }

    clearStatusBanner();
    clearTutorialHint();
    telemetry.track("tutorial_progress", {
      step: "negative_explainer_opened"
    });
    setActiveMechanicSpotlight({
      kind: "negative",
      handTileId: negativeHandTile.id
    });
    setTimerSpotlightPauseStartedAt(Date.now());
  }, [
    activeMechanicSpotlight,
    game.gameOver,
    game.roundNumber,
    game.score,
    isLiveGameplayScreen,
    negativeHandTile,
    onboarding.hasSeenNegativeExplanation,
    telemetry
  ]);

  useEffect(() => {
    const firstIncrementLane = game.increment.lanes[0];
    if (
      !isLiveGameplayScreen ||
      game.gameOver ||
      onboarding.hasSeenIncrementExplanation ||
      !isIncrementEnabled({ roundNumber: game.roundNumber, score: game.score }) ||
      firstIncrementLane?.tileId === undefined ||
      firstIncrementLane.tileId === null ||
      activeMechanicSpotlight !== null
    ) {
      return;
    }

    clearStatusBanner();
    clearTutorialHint();
    telemetry.track("tutorial_progress", {
      step: "increment_explainer_opened"
    });
    setActiveMechanicSpotlight({
      kind: "increment",
      boardTileId: firstIncrementLane.tileId
    });
    setTimerSpotlightPauseStartedAt(Date.now());
  }, [
    activeMechanicSpotlight,
    game.gameOver,
    game.increment.lanes,
    game.score,
    game.roundNumber,
    isLiveGameplayScreen,
    onboarding.hasSeenIncrementExplanation,
    telemetry
  ]);

  useEffect(() => {
    if (
      !isLiveGameplayScreen ||
      game.gameOver ||
      !onboarding.hasSeenFlowExplanation ||
      onboarding.hasSeenRefillExplanation ||
      !isRefillEnabled({ roundNumber: game.roundNumber, score: game.score }) ||
      activeMechanicSpotlight !== null
    ) {
      return;
    }

    clearStatusBanner();
    clearTutorialHint();
    telemetry.track("tutorial_progress", {
      step: "refill_explainer_opened"
    });
    setActiveMechanicSpotlight({ kind: "refill" });
    setTimerSpotlightPauseStartedAt(Date.now());
  }, [
    activeMechanicSpotlight,
    game.gameOver,
    game.roundNumber,
    game.score,
    isLiveGameplayScreen,
    onboarding.hasSeenFlowExplanation,
    onboarding.hasSeenRefillExplanation,
    telemetry
  ]);

  useEffect(() => {
    if (pendingLeaderboardSubmission === null) {
      return;
    }

    let cancelled = false;
    setIsSavingLeaderboardScore(true);
    setLeaderboardSaveError(null);
    setLeaderboardSaveNotice(null);

    void (async () => {
      try {
        const result = await submitLeaderboardRun({
          actions: pendingLeaderboardSubmission.actions,
          appVersion: pendingLeaderboardSubmission.appVersion,
          completedAt: pendingLeaderboardSubmission.completedAt,
          gameRulesVersion: GAME_RULES_VERSION,
          playerName: pendingLeaderboardSubmission.playerName,
          runId: pendingLeaderboardSubmission.runId
        });

        if (cancelled) {
          return;
        }

        setHighlightLeaderboardEntries(buildHighlightEntries(result.entries));
        setCurrentUserId((existingUserId) => existingUserId ?? result.entries[0]?.userId ?? null);
        setLeaderboardRefreshKey((currentValue) => currentValue + 1);
        setLeaderboardSaveNotice(getLeaderboardSaveReviewMessage(result.flaggedReason));
        telemetry.track("leaderboard_save_succeeded", {
          accepted: result.accepted,
          entryCount: result.entries.length,
          flagged: result.flaggedReason !== null,
          flaggedReason: result.flaggedReason
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        const errorMessage = getErrorMessage(error, "Could not save score. Try again.");

        telemetry.captureError("leaderboard_save", error, {
          runId: pendingLeaderboardSubmission.runId
        });
        telemetry.track("leaderboard_save_failed", {
          runId: pendingLeaderboardSubmission.runId,
          message: errorMessage
        });
        setLeaderboardSaveNotice(null);
        setLeaderboardSaveError(errorMessage);
      } finally {
        if (!cancelled) {
          setIsSavingLeaderboardScore(false);
          setPendingLeaderboardSubmission((currentSubmission) =>
            currentSubmission?.token === pendingLeaderboardSubmission.token
              ? null
              : currentSubmission
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingLeaderboardSubmission, telemetry]);

  const createFloatingScores = (
    scoreEvents: ScoreEvent[],
    boardSize: number
  ): FloatingScore[] => {
    return scoreEvents.map((scoreEvent, scoreEventIndex) => ({
      id: nextFloatingScoreId(),
      value: scoreEvent.awardedPoints,
      kind: scoreEvent.type,
      lane: nextFloatingScoreLane(),
      delayMs: scoreEventIndex * 70,
      origin:
        scoreEvent.origin === "tile" && scoreEvent.boardIndex !== null
          ? {
              kind: "tile",
              boardIndex: scoreEvent.boardIndex,
              boardSize
            }
          : {
              kind: "board_center"
            }
    }));
  };

  const clearFloatingScores = () => {
    floatingScoreTimersRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    floatingScoreTimersRef.current.clear();
    setModel((previousModel) => {
      if (previousModel.floatingScores.length === 0) {
        return previousModel;
      }

      return {
        ...previousModel,
        floatingScores: []
      };
    });
  };

  const clearTransientEffects = () => {
    if (moveTimerRef.current !== null) {
      window.clearTimeout(moveTimerRef.current);
      moveTimerRef.current = null;
    }

    if (shakeTimerRef.current !== null) {
      window.clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = null;
    }

    pendingPlayedHandIndexRef.current = null;
    recordedRunActionsRef.current = [];
    recordedRunPauseWindowsRef.current = [];
    setPendingMove(null);
    setScreenShake(false);
    setIsResolvingMove(false);
    clearFloatingScores();
    clearStatusBanner();
    clearTutorialHint();
    clearGameOverReveal();
    setActiveMechanicSpotlight(null);
    setIsRulesOpen(false);
    setIsTutorialRoundActive(false);
    setTimerSpotlightPauseStartedAt(null);
    setShowDeadlockAnnouncement(false);
    setShowGameOverModal(false);
    setPendingLeaderboardSubmission(null);

    if (flowExpiryTimerRef.current !== null) {
      window.clearTimeout(flowExpiryTimerRef.current);
      flowExpiryTimerRef.current = null;
    }

    if (refillExpiryTimerRef.current !== null) {
      window.clearTimeout(refillExpiryTimerRef.current);
      refillExpiryTimerRef.current = null;
    }

    incrementExpiryTimersRef.current.forEach(({ timeoutId }) => {
      window.clearTimeout(timeoutId);
    });
    incrementExpiryTimersRef.current.clear();
  };

  const handleOpenSupportLink = (
    location: "title" | "settings" | "game_over",
    kind: SupportLinkKind,
    url: string
  ) => {
    if (kind === "feedback") {
      const nextFeedbackPromptState = saveFeedbackPromptState(
        markFeedbackSubmitted(feedbackPromptState)
      );
      setFeedbackPromptState(nextFeedbackPromptState);
      setActiveFeedbackPromptThreshold(null);
      telemetry.track("feedback_submitted", {
        location,
        promptThreshold: activeFeedbackPromptThreshold,
        url
      });
    }

    telemetry.track("support_link_clicked", {
      kind,
      location,
      url
    });
  };

  const createRunSession = async (guidedRun: boolean): Promise<{
    runId: string;
    runSeed: string;
    session: ActiveRunSession | null;
  }> => {
    const localRunId = createRunId();
    pendingRunSessionErrorRef.current = null;

    if (!runtimeConfig.leaderboardEnabled) {
      return {
        runId: localRunId,
        runSeed: localRunId,
        session: null
      };
    }

    try {
      const session = await startLeaderboardRun({
        appVersion: APP_VERSION,
        gameRulesVersion: GAME_RULES_VERSION,
        guidedRun
      });
      const clientStartedAt = Date.now();
      return {
        runId: session.runId,
        runSeed: session.runSeed,
        session: {
          clientStartedAt,
          guidedRun,
          runId: session.runId,
          runSeed: session.runSeed,
          startedAt: session.startedAt
        }
      };
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Verified leaderboard session could not be created."
      );
      pendingRunSessionErrorRef.current = message;
      telemetry.captureError("start_run", error, {
        guidedRun
      });
      return {
        runId: localRunId,
        runSeed: localRunId,
        session: null
      };
    }
  };

  const isPreparedRunUsable = (preparedRun: PreparedRun | null, guidedRun: boolean) => {
    if (preparedRun === null || preparedRun.guidedRun !== guidedRun) {
      return false;
    }

    return Date.now() - preparedRun.preparedAt < PREPARED_RUN_MAX_AGE_MS;
  };

  const prepareRunInBackground = (guidedRun: boolean): Promise<PreparedRun> => {
    if (isPreparedRunUsable(preparedRunRef.current, guidedRun)) {
      return Promise.resolve(preparedRunRef.current as PreparedRun);
    }

    const activeRequest = preparedRunRequestRef.current;
    if (activeRequest !== null && activeRequest.guidedRun === guidedRun) {
      return activeRequest.promise;
    }

    const request = createRunSession(guidedRun).then(({ runId, runSeed, session }) => {
      const preparedRun: PreparedRun = {
        guidedRun,
        preparedAt: Date.now(),
        runId,
        runSeed,
        session
      };

      preparedRunRef.current = preparedRun;
      return preparedRun;
    });

    preparedRunRequestRef.current = {
      guidedRun,
      promise: request
    };

    return request.finally(() => {
      if (preparedRunRequestRef.current?.promise === request) {
        preparedRunRequestRef.current = null;
      }
    });
  };

  const takePreparedRun = async (guidedRun: boolean): Promise<PreparedRun | null> => {
    if (isPreparedRunUsable(preparedRunRef.current, guidedRun)) {
      const preparedRun = preparedRunRef.current;
      preparedRunRef.current = null;
      return preparedRun;
    }

    preparedRunRef.current = null;

    const activeRequest = preparedRunRequestRef.current;
    if (activeRequest !== null && activeRequest.guidedRun === guidedRun) {
      const preparedRun = await activeRequest.promise;
      if (isPreparedRunUsable(preparedRun, guidedRun)) {
        preparedRunRef.current = null;
        return preparedRun;
      }
    }

    return null;
  };

  useEffect(() => {
    if (!runtimeConfig.leaderboardEnabled) {
      preparedRunRef.current = null;
      preparedRunRequestRef.current = null;
      return;
    }

    const guidedRun = getBackgroundPreparedRunGuidance({
      hasActiveRunSession: activeRunSession !== null,
      isStartingRun,
      isTutorialRoundActive,
      onboarding,
      screen
    });

    if (guidedRun === null) {
      return;
    }

    void prepareRunInBackground(guidedRun);
  }, [
    activeRunSession,
    isTutorialRoundActive,
    isStartingRun,
    onboarding,
    runtimeConfig.leaderboardEnabled,
    screen
  ]);

  const handleSelectHandTile = (handIndex: number) => {
    if (game.gameOver || isInteractionLocked || game.hand[handIndex] === undefined) {
      return;
    }

    markTutorialActivity();

    if (game.selectedHandIndex === handIndex) {
      setModel((previousModel) => ({
        ...previousModel,
        game: {
          ...previousModel.game,
          selectedHandIndex: null
        }
      }));
      return;
    }

    applyGameAction({ type: "select_hand_tile", handIndex });
  };

  const handleTapBoardTile = (boardIndex: number) => {
    if (game.gameOver || isInteractionLocked || game.selectedHandIndex === null) {
      return;
    }

    const playedAt = Date.now();
    const selectedValue = game.hand[game.selectedHandIndex];
    const boardTile = game.board[boardIndex];

    if (selectedValue === undefined || boardTile === undefined) {
      return;
    }

    markTutorialActivity();

    const resolution = resolveMove(selectedValue, boardTile.value);
    if (resolution.outcome === "illegal") {
      showStatusBanner({
        title: selectedValue < 0 ? "Too low" : "Too high",
        body: selectedValue < 0 ? "That move goes below 0." : "That move goes over 10.",
        tone: "warning"
      });
      applyGameAction({ type: "tap_board_tile", boardIndex, playedAt });
      return;
    }

    const soundEffectId = getMoveSoundEffectId(resolution.outcome);
    if (soundEffectId !== null) {
      playSoundEffect(soundEffectId);
    }

    if (flowExpiryTimerRef.current !== null) {
      window.clearTimeout(flowExpiryTimerRef.current);
      flowExpiryTimerRef.current = null;
    }

    pendingPlayedHandIndexRef.current = game.selectedHandIndex;
    setPendingMove({
      boardIndex,
      outcome: resolution.outcome,
      particleColor: getTileColor(boardTile.value).hex
    });
    setIsResolvingMove(true);

    if (resolution.outcome === "build_to_ten_clear") {
      setScreenShake(true);
      if (shakeTimerRef.current !== null) {
        window.clearTimeout(shakeTimerRef.current);
      }

      shakeTimerRef.current = window.setTimeout(() => {
        setScreenShake(false);
        shakeTimerRef.current = null;
      }, 150);
    }

    if (moveTimerRef.current !== null) {
      window.clearTimeout(moveTimerRef.current);
    }

    const moveDelay = resolution.clearsTile ? 190 : 105;
    moveTimerRef.current = window.setTimeout(() => {
      applyGameAction({ type: "tap_board_tile", boardIndex, playedAt });
      setPendingMove(null);
      setIsResolvingMove(false);
      moveTimerRef.current = null;
    }, moveDelay);
  };

  const restartRun = async (
    origin: "start" | "restart",
    onboardingState = onboarding,
    preparedRun: PreparedRun | null = null,
    options: {
      preserveTutorialRoundActive?: boolean;
    } = {}
  ) => {
    clearTransientEffects();
    if (options.preserveTutorialRoundActive) {
      setIsTutorialRoundActive(true);
    }
    setIsSettingsOpen(false);
    setLeaderboardSaveError(null);
    setLeaderboardSaveNotice(null);
    setActiveFeedbackPromptThreshold(null);
    const nextGuidedRun = shouldUseGuidedRun(onboardingState);
    const nextRun =
      preparedRun !== null && isPreparedRunUsable(preparedRun, nextGuidedRun)
        ? preparedRun
        : await createRunSession(nextGuidedRun);
    recordedRunActionsRef.current = [];
    recordedRunPauseWindowsRef.current = [];
    setRunGenerator(nextRun.runSeed);
    setActiveRunId(nextRun.runId);
    setActiveRunSession(nextRun.session);
    setIsGuidedRun(nextGuidedRun);
    telemetry.track(
      "run_started",
      {
        firstRun: origin === "start" && !onboardingState.hasCompletedFirstRun,
        guidedRun: nextGuidedRun,
        origin
      },
      {
        runId: nextRun.runId,
        screen: "game"
      }
    );
    applyGameAction({
      type: "restart_run",
      initialDeal: nextGuidedRun ? FIRST_RUN_INITIAL_DEAL : null
    });
  };

  const handleRestart = () => {
    void restartRun("restart");
  };

  const handleStartRun = () => {
    if (isStartingRun) {
      return;
    }

    setHighlightLeaderboardEntries({});
    if (shouldShowRoundZeroTutorial(onboarding)) {
      clearTransientEffects();
      setIsSettingsOpen(false);
      setLeaderboardSaveError(null);
      setLeaderboardSaveNotice(null);
      setActiveRunSession(null);
      setIsTutorialRoundActive(true);
      setScreen("game");
      telemetry.track("tutorial_progress", {
        step: "round_zero_started"
      });
      return;
    }

    setIsStartingRun(true);
    void (async () => {
      try {
        const preparedRun = await takePreparedRun(shouldUseGuidedRun(onboarding));
        await restartRun("start", onboarding, preparedRun);
        setScreen("game");
        activateGameplayMusic();
      } finally {
        setIsStartingRun(false);
      }
    })();
  };

  const handleCompleteRoundZeroTutorial = () => {
    if (isStartingRun) {
      return;
    }

    const nextOnboarding = saveOnboardingState({
      ...onboarding,
      hasSeenTutorial: true
    });
    setOnboarding(nextOnboarding);
    telemetry.track("tutorial_progress", {
      step: "round_zero_completed"
    });
    setIsStartingRun(true);
    void (async () => {
      try {
        const preparedRun = await takePreparedRun(shouldUseGuidedRun(nextOnboarding));
        await restartRun("start", nextOnboarding, preparedRun, {
          preserveTutorialRoundActive: true
        });
        setIsTutorialRoundActive(false);
        activateGameplayMusic();
      } finally {
        setIsStartingRun(false);
      }
    })();
  };

  const handleSaveProfile = (nextProfileName: string) => {
    setProfileName(
      saveProfileName(nextProfileName, {
        currentProfileName: profileName
      })
    );
  };

  const syncMusicVolume = (nextMusicVolume: number) => {
    const savedMusicVolume = saveMusicVolume(nextMusicVolume);
    if (savedMusicVolume > 0) {
      lastAudibleMusicVolumeRef.current = savedMusicVolume;
    }

    setMusicVolumePercent(savedMusicVolume);

    const nextMusicPlaybackState = getMusicPlaybackState({
      musicVolumePercent: savedMusicVolume
    });
    if (musicControllerRef.current !== null || shouldMusicBeActive(nextMusicPlaybackState)) {
      ensureMusicController().syncPlayback(nextMusicPlaybackState);
    }
  };

  const handleMusicVolumeChange = (nextMusicVolume: number) => {
    syncMusicVolume(nextMusicVolume);
  };

  const syncSoundEffectVolume = (nextSfxVolume: number) => {
    const savedSfxVolume = saveSoundEffectVolume(nextSfxVolume);
    if (savedSfxVolume > 0) {
      lastAudibleSfxVolumeRef.current = savedSfxVolume;
    }

    sfxVolumePercentRef.current = savedSfxVolume;
    setSfxVolumePercent(savedSfxVolume);

    if (soundEffectControllerRef.current !== null) {
      soundEffectControllerRef.current.syncVolume(savedSfxVolume);
    }
  };

  const handleSfxVolumeChange = (nextSfxVolume: number) => {
    syncSoundEffectVolume(nextSfxVolume);
  };

  const handleToggleAudioMuted = () => {
    const nextVolumes = toggleAudioMutedVolumes({
      musicVolumePercent,
      sfxVolumePercent,
      lastAudibleMusicVolumePercent: lastAudibleMusicVolumeRef.current,
      lastAudibleSfxVolumePercent: lastAudibleSfxVolumeRef.current
    });

    syncMusicVolume(nextVolumes.musicVolumePercent);
    syncSoundEffectVolume(nextVolumes.sfxVolumePercent);
  };

  const handleDismissTimerSpotlight = () => {
    if (activeMechanicSpotlight === null) {
      return;
    }

    if (timerSpotlightPauseStartedAt !== null) {
      const pausedAt = Date.now();
      recordVerifiedRunPauseWindow({
        startedAt: timerSpotlightPauseStartedAt,
        endedAt: pausedAt
      });
      applyGameAction({
        type: "shift_time_windows",
        deltaMs: pausedAt - timerSpotlightPauseStartedAt
      });
      setTimerSpotlightPauseStartedAt(null);
    }

    if (activeMechanicSpotlight.kind === "flow" && !onboarding.hasSeenFlowExplanation) {
      updateOnboarding({ hasSeenFlowExplanation: true });
    }

    if (activeMechanicSpotlight.kind === "negative" && !onboarding.hasSeenNegativeExplanation) {
      updateOnboarding({ hasSeenNegativeExplanation: true });
    }

    if (activeMechanicSpotlight.kind === "increment" && !onboarding.hasSeenIncrementExplanation) {
      updateOnboarding({ hasSeenIncrementExplanation: true });
    }

    if (activeMechanicSpotlight.kind === "refill" && !onboarding.hasSeenRefillExplanation) {
      updateOnboarding({ hasSeenRefillExplanation: true });
    }

    setActiveMechanicSpotlight(null);
  };

  const handleCloseRulesModal = () => {
    setIsRulesOpen(false);
  };

  const handleOpenRules = () => {
    clearStatusBanner();
    clearTutorialHint();
    setIsSettingsOpen(false);
    setIsRulesOpen(true);
  };

  const handleReturnToTitle = () => {
    clearTransientEffects();
    setIsStartingRun(false);
    setIsSettingsOpen(false);
    setActiveFeedbackPromptThreshold(null);
    setActiveRunSession(null);
    setScreen("title");
  };

  const handleOpenFeedbackPrompt = () => {
    if (runtimeConfig.feedbackUrl.trim().length === 0) {
      return;
    }

    handleOpenSupportLink("game_over", "feedback", runtimeConfig.feedbackUrl);

    if (runtimeConfig.feedbackUrl.startsWith("mailto:")) {
      window.location.href = runtimeConfig.feedbackUrl;
      return;
    }

    window.open(runtimeConfig.feedbackUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <motion.main
      className={`app-shell theme-${theme}`}
      animate={screenShake ? { x: [-3, 3, -3, 0] } : { x: 0 }}
      transition={{ duration: 0.15 }}
    >
      <div className="theme-background" aria-hidden="true" />
      <>
        <div className={`app-content${screen === "game" ? "" : " app-content-menu"}`}>
          <AnimatePresence mode="wait">
            {screen === "title" ? (
              <TitleScreen
                key="title"
                discordUrl={runtimeConfig.discordUrl}
                feedbackUrl={runtimeConfig.feedbackUrl}
                isLeaderboardEnabled={runtimeConfig.leaderboardEnabled}
                supportEmail={runtimeConfig.supportEmail}
                onStartRun={handleStartRun}
                onOpenLink={(kind, url) => handleOpenSupportLink("title", kind, url)}
                onOpenSettings={() => setIsSettingsOpen(true)}
                onOpenLeaderboard={() => {
                  setHighlightLeaderboardEntries({});
                  setScreen("leaderboard");
                }}
              />
            ) : null}

            {screen === "leaderboard" ? (
              <LeaderboardScreen
                key="leaderboard"
                activeMetric={activeLeaderboardMetric}
                activePeriod={activeLeaderboardPeriod}
                currentUserId={currentUserId}
                currentProfileName={profileName}
                highlightEntries={highlightLeaderboardEntries}
                refreshKey={leaderboardRefreshKey}
                onMetricChange={setActiveLeaderboardMetric}
                onPeriodChange={setActiveLeaderboardPeriod}
                onSaveProfileName={handleSaveProfile}
                onBack={() => {
                  setHighlightLeaderboardEntries({});
                  setScreen("title");
                }}
              />
            ) : null}

            {screen === "game" ? (
              isTutorialRoundActive ? (
                <motion.div
                  key="tutorial-round"
                  className="game-screen tutorial-game-screen"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={GAME_SCREEN_TRANSITION}
                >
                  <header className="top-bar tutorial-top-bar" aria-label="Tutorial">
                    <div className="top-bar-side top-bar-side-start" aria-hidden="true" />
                    <div className="top-bar-center">
                      <div className={`top-bar-stat top-bar-stat-tutorial panel panel-${theme}`}>
                        <p className="tutorial-top-bar-title">Tutorial</p>
                      </div>
                    </div>
                    <div className="top-bar-side top-bar-side-end" aria-hidden="true" />
                  </header>
                  <TutorialRound
                    theme={theme}
                    onComplete={handleCompleteRoundZeroTutorial}
                    onPlaySoundEffect={playSoundEffect}
                    onTrackProgress={(step) => {
                      telemetry.track("tutorial_progress", {
                        step: `round_zero_${step}`
                      });
                    }}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="gameplay"
                  className="game-screen"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={GAME_SCREEN_TRANSITION}
                >
                  {!game.gameOver || showDeadlockAnnouncement ? (
                    <Header
                      isAudioMuted={audioMuted}
                      roundNumber={game.roundNumber}
                      score={game.score}
                      theme={theme}
                      onOpenSettings={() => setIsSettingsOpen(true)}
                      onToggleAudioMuted={handleToggleAudioMuted}
                    />
                  ) : null}

                  <section
                    className={`board-stage${
                      isBoardMechanicSpotlightOpen ? " board-stage-flow-spotlight" : ""
                    }`}
                  >
                    <AnimatePresence initial={false}>
                      {!game.gameOver && statusBanner !== null ? (
                        <div className="status-banner-layer">
                          <StatusBanner
                            title={statusBanner.title}
                            body={statusBanner.body}
                            tone={statusBanner.tone}
                          />
                        </div>
                      ) : null}
                    </AnimatePresence>
                    <Board
                      board={game.board}
                      flowCount={game.flow.count}
                      incrementLanes={boardIncrementLanes}
                      timerDisplay={boardTimerDisplay}
                      timerPausedAt={isTimerPaused ? timerSpotlightPauseStartedAt : null}
                      timerSpotlightKind={
                        activeMechanicSpotlight?.kind === "flow" ||
                        activeMechanicSpotlight?.kind === "refill"
                          ? activeMechanicSpotlight.kind
                          : null
                      }
                      incrementSpotlightTileId={
                        activeMechanicSpotlight?.kind === "increment"
                          ? activeMechanicSpotlight.boardTileId
                          : null
                      }
                      floatingScores={model.floatingScores}
                      selectedHandValue={selectedHandValue}
                      gameOver={game.gameOver}
                      isResolvingMove={isInteractionLocked}
                      pendingMove={pendingMove}
                      theme={theme}
                      onSelectTile={handleTapBoardTile}
                      onDismissTimerSpotlight={handleDismissTimerSpotlight}
                    />
                    <AnimatePresence>
                      {showDeadlockAnnouncement ? (
                        <motion.div
                          className="deadlock-announcement"
                          initial={{ opacity: 0, y: 16, scale: 0.96 }}
                          animate={{
                            opacity: [0, 1, 1, 0],
                            y: [16, 0, 0, -10],
                            scale: [0.96, 1, 1, 1.02]
                          }}
                          exit={{ opacity: 0, y: -8, scale: 1.01 }}
                          transition={{
                            duration: GAME_OVER_ANNOUNCEMENT_MS / 1000,
                            ease: "easeOut"
                          }}
                        >
                          <span className="deadlock-announcement-copy">No legal moves left</span>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </section>

                  <footer className="footer-stage">
                    <div className="hand-stage">
                      <HandView
                        hand={handTiles}
                        selectedHandIndex={game.selectedHandIndex}
                        negativeSpotlightTileId={
                          activeMechanicSpotlight?.kind === "negative"
                            ? activeMechanicSpotlight.handTileId
                            : null
                        }
                        gameOver={game.gameOver}
                        isResolvingMove={isInteractionLocked}
                        hintText={tutorialHintText}
                        theme={theme}
                        onSelectHandTile={handleSelectHandTile}
                        onDismissNegativeSpotlight={handleDismissTimerSpotlight}
                      />
                    </div>
                  </footer>

                  <GameOverModal
                    open={showGameOverModal}
                    summary={game.lastRunSummary}
                    theme={theme}
                    leaderboardEnabled={runtimeConfig.leaderboardEnabled}
                    currentUserId={currentUserId}
                    currentProfileName={profileName}
                    latestScoreEntry={highlightLeaderboardEntries.score ?? null}
                    highlightedScoreEntryId={highlightLeaderboardEntries.score?.id ?? null}
                    leaderboardRefreshKey={leaderboardRefreshKey}
                    isSavingScore={isSavingLeaderboardScore}
                    leaderboardSaveError={leaderboardSaveError}
                    leaderboardSaveNotice={leaderboardSaveNotice}
                    feedbackPromptThreshold={activeFeedbackPromptThreshold}
                    onDismissFeedbackPrompt={() => setActiveFeedbackPromptThreshold(null)}
                    onOpenFeedbackPrompt={handleOpenFeedbackPrompt}
                    onRestart={handleRestart}
                    onMainMenu={handleReturnToTitle}
                  />
                </motion.div>
              )
            ) : null}
          </AnimatePresence>
        </div>

        <SettingsModal
          buildLabel={buildLabel}
          discordUrl={runtimeConfig.discordUrl}
          environmentLabel={runtimeConfig.environmentLabel}
          feedbackUrl={runtimeConfig.feedbackUrl}
          musicVolumePercent={musicVolumePercent}
          sfxVolumePercent={sfxVolumePercent}
          open={isSettingsOpen}
          supportEmail={runtimeConfig.supportEmail}
          theme={theme}
          profileName={profileName}
          closeLabel={screen === "game" ? "Resume" : "Back"}
          onClose={() => setIsSettingsOpen(false)}
          onMusicVolumeChange={handleMusicVolumeChange}
          onSfxVolumeChange={handleSfxVolumeChange}
          onOpenLink={(kind, url) => handleOpenSupportLink("settings", kind, url)}
          onSaveProfileName={handleSaveProfile}
          onMainMenu={screen === "game" ? handleReturnToTitle : undefined}
          onViewRules={screen === "game" ? handleOpenRules : undefined}
          onRestart={screen === "game" ? handleRestart : undefined}
        />

        <InfoModal
          open={isRulesOpen}
          theme={theme}
          title="How To Play"
          body="Match numbers or add to 10 to clear. Sums under 10 build. Negative tiles reduce and clear at 0. Charged tiles can increment over time. Clear the board to advance."
          buttonLabel="OK"
          onClose={handleCloseRulesModal}
        />
      </>
    </motion.main>
  );
}
